/* ==========================================================================
   Momentum — core runtime
   Utilities, focus management, section rendering, cart store, pub/sub
   ========================================================================== */
(function () {
  'use strict';

  const T = window.theme || {};
  T.routes = T.routes || {};
  T.strings = T.strings || {};
  T.settings = T.settings || {};

  /* ----------------------------- utils ---------------------------------- */

  const utils = {
    debounce(fn, wait) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    throttle(fn, limit) {
      let waiting = false;
      return function (...args) {
        if (waiting) return;
        fn.apply(this, args);
        waiting = true;
        setTimeout(() => (waiting = false), limit);
      };
    },

    formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const fmt = format || T.settings.moneyFormat || '${{amount}}';
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;

      function pad(num, size) {
        let s = String(num);
        while (s.length < size) s = '0' + s;
        return s;
      }

      function thousands(number, precision, thousandSep, decimalSep) {
        if (isNaN(number) || number === null) return 0;
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousandSep || ','));
        const cents = parts[1] ? (decimalSep || '.') + parts[1] : '';
        return dollars + cents;
      }

      let value = '';
      const match = fmt.match(placeholder);
      if (!match) return fmt;

      switch (match[1]) {
        case 'amount': value = thousands(cents, 2); break;
        case 'amount_no_decimals': value = thousands(cents, 0); break;
        case 'amount_with_comma_separator': value = thousands(cents, 2, '.', ','); break;
        case 'amount_with_period_and_space_separator': value = thousands(cents, 2, ' ', '.'); break;
        case 'amount_no_decimals_with_comma_separator': value = thousands(cents, 0, '.', ','); break;
        case 'amount_no_decimals_with_space_separator': value = thousands(cents, 0, ' '); break;
        case 'amount_with_space_separator': value = thousands(cents, 2, ' ', ','); break;
        case 'amount_with_apostrophe_separator': value = thousands(cents, 2, "'"); break;
        default: value = thousands(cents, 2);
      }
      return fmt.replace(placeholder, value);
    },

    fetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
      };
    },

    /* Section Rendering API */
    async getSectionHTML(url, sectionId) {
      const sep = url.includes('?') ? '&' : '?';
      const res = await fetch(`${url}${sep}section_id=${sectionId}`);
      if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);
      const text = await res.text();
      return new DOMParser().parseFromString(text, 'text/html');
    },

    replaceInnerHTML(selector, html) {
      const target = document.querySelector(selector);
      if (!target || html == null) return;
      target.innerHTML = html;
    },

    announce(message) {
      const region = document.getElementById('LiveRegion');
      if (!region) return;
      region.textContent = '';
      setTimeout(() => (region.textContent = message), 60);
    },

    toast(message, variant) {
      const stack = document.getElementById('ToastStack');
      if (!stack) return;
      const el = document.createElement('div');
      el.className = 'toast' + (variant === 'error' ? ' toast--error' : '');
      el.textContent = message;
      stack.appendChild(el);
      utils.announce(message);
      setTimeout(() => {
        el.classList.add('is-leaving');
        el.addEventListener('animationend', () => el.remove(), { once: true });
      }, 3600);
    },

    /* ---------------------- focus management -------------------------- */

    focusableSelector:
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe',

    trapFocus(container, elementToFocus) {
      const elements = Array.from(container.querySelectorAll(utils.focusableSelector)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      const first = elements[0];
      const last = elements[elements.length - 1];

      utils.removeTrapFocus();

      utils._trapHandler = function (event) {
        if (event.code !== 'Tab' && event.key !== 'Tab') return;
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first && first.focus();
        }
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last && last.focus();
        }
      };
      utils._trapContainer = container;
      document.addEventListener('keydown', utils._trapHandler);
      const target = elementToFocus || first || container;
      if (target) {
        if (!target.hasAttribute('tabindex') && target === container) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    },

    removeTrapFocus(elementToFocus) {
      if (utils._trapHandler) document.removeEventListener('keydown', utils._trapHandler);
      utils._trapHandler = null;
      utils._trapContainer = null;
      if (elementToFocus) elementToFocus.focus({ preventScroll: true });
    },

    /* ---------------------- scroll lock ------------------------------- */

    _lockCount: 0,
    lockScroll() {
      utils._lockCount += 1;
      if (utils._lockCount > 1) return;
      utils._scrollY = window.scrollY;
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sbw > 0 ? `${sbw}px` : '';
      document.body.classList.add('overflow-hidden');
    },
    unlockScroll() {
      utils._lockCount = Math.max(0, utils._lockCount - 1);
      if (utils._lockCount > 0) return;
      document.body.classList.remove('overflow-hidden');
      document.body.style.paddingRight = '';
    },

    /* ---------------------- overlay ----------------------------------- */

    showOverlay(onClick) {
      const overlay = document.querySelector('[data-overlay]');
      if (!overlay) return;
      overlay.hidden = false;
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
      overlay._handler = onClick;
      overlay.addEventListener('click', onClick, { once: true });
    },
    hideOverlay() {
      const overlay = document.querySelector('[data-overlay]');
      if (!overlay) return;
      if (overlay._handler) overlay.removeEventListener('click', overlay._handler);
      overlay.classList.remove('is-visible');
      setTimeout(() => {
        if (!overlay.classList.contains('is-visible')) overlay.hidden = true;
      }, 300);
    },

    /* ---------------------- storage (safe) ---------------------------- */

    store: {
      get(key, fallback = null) {
        try {
          const raw = localStorage.getItem(key);
          return raw === null ? fallback : JSON.parse(raw);
        } catch (e) {
          return fallback;
        }
      },
      set(key, value) {
        try {
          localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
          /* private mode / quota */
        }
      },
      remove(key) {
        try { localStorage.removeItem(key); } catch (e) {}
      }
    },

    /* ---------------------- misc -------------------------------------- */

    onDocumentReady(fn) {
      if (document.readyState !== 'loading') fn();
      else document.addEventListener('DOMContentLoaded', fn, { once: true });
    },

    isEditor() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    },

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  };

  /* ----------------------------- pub/sub -------------------------------- */

  const listeners = {};
  const events = {
    CART_UPDATE: 'cart:update',
    CART_ERROR: 'cart:error',
    VARIANT_CHANGE: 'variant:change',
    QUANTITY_CHANGE: 'quantity:change'
  };

  function subscribe(event, cb) {
    if (!listeners[event]) listeners[event] = [];
    listeners[event].push(cb);
    return () => {
      listeners[event] = listeners[event].filter((fn) => fn !== cb);
    };
  }

  function publish(event, payload) {
    (listeners[event] || []).forEach((cb) => {
      try { cb(payload); } catch (e) { console.error(e); }
    });
  }

  /* ----------------------------- cart store ----------------------------- */

  const cart = {
    state: null,
    inflight: null,

    sectionsToRender() {
      const ids = new Set();
      document.querySelectorAll('[data-cart-section]').forEach((el) => {
        const id = el.dataset.cartSection;
        if (id) ids.add(id);
      });
      return Array.from(ids);
    },

    async add(items, opts = {}) {
      const sections = this.sectionsToRender();
      const body = {
        items: Array.isArray(items) ? items : [items],
        sections,
        sections_url: window.location.pathname
      };
      if (opts.attributes) body.attributes = opts.attributes;

      const res = await fetch(T.routes.cart_add, {
        ...utils.fetchConfig('javascript'),
        body: JSON.stringify(body)
      });
      const data = await res.json();

      if (data.status) {
        publish(events.CART_ERROR, data);
        const err = new Error(data.description || data.message || T.strings.cartError);
        err.data = data;
        throw err;
      }

      this.renderSections(data.sections);
      await this.refresh(false);
      publish(events.CART_UPDATE, { cart: this.state, added: data, source: opts.source });
      return data;
    },

    async change(line, quantity, opts = {}) {
      const body = {
        line: String(line),
        quantity,
        sections: this.sectionsToRender(),
        sections_url: window.location.pathname
      };
      const res = await fetch(T.routes.cart_change, {
        ...utils.fetchConfig('javascript'),
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.status && data.status !== 200) {
        publish(events.CART_ERROR, data);
        throw new Error(data.description || data.message || T.strings.cartError);
      }
      this.state = data;
      this.renderSections(data.sections);
      publish(events.CART_UPDATE, { cart: this.state, source: opts.source || 'change' });
      return data;
    },

    async update(payload) {
      const body = {
        ...payload,
        sections: this.sectionsToRender(),
        sections_url: window.location.pathname
      };
      const res = await fetch(T.routes.cart_update, {
        ...utils.fetchConfig('javascript'),
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.status && data.status !== 200) {
        publish(events.CART_ERROR, data);
        throw new Error(data.description || data.message || T.strings.cartError);
      }
      this.state = data;
      this.renderSections(data.sections);
      publish(events.CART_UPDATE, { cart: this.state, source: 'update' });
      return data;
    },

    async refresh(notify = true) {
      const res = await fetch(`${T.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      this.state = await res.json();
      if (notify) publish(events.CART_UPDATE, { cart: this.state, source: 'refresh' });
      return this.state;
    },

    renderSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
          const selector = target.dataset.cartSectionSelector;
          const source = selector
            ? doc.querySelector(selector)
            : doc.querySelector(`[data-cart-section="${id}"]`) || doc.querySelector('.shopify-section');
          if (source) target.innerHTML = source.innerHTML;
        });
      });
    }
  };

  /* ----------------------- recently viewed ------------------------------ */

  const recentlyViewed = {
    key: 'momentum:recently-viewed',
    max: 20,
    track(id, handle) {
      if (!T.settings.recentlyViewed || !id) return;
      let list = utils.store.get(this.key, []);
      if (!Array.isArray(list)) list = [];
      list = list.filter((item) => item.id !== id);
      list.unshift({ id, handle, at: Date.now() });
      utils.store.set(this.key, list.slice(0, this.max));
    },
    get(excludeId) {
      let list = utils.store.get(this.key, []);
      if (!Array.isArray(list)) list = [];
      return list.filter((item) => item && item.id && item.id !== excludeId);
    }
  };

  /* ----------------------- base custom element -------------------------- */

  class MomentumElement extends HTMLElement {
    constructor() {
      super();
      this._cleanups = [];
    }
    on(target, type, handler, opts) {
      target.addEventListener(type, handler, opts);
      this._cleanups.push(() => target.removeEventListener(type, handler, opts));
    }
    cleanup(fn) { this._cleanups.push(fn); }
    disconnectedCallback() {
      this._cleanups.forEach((fn) => { try { fn(); } catch (e) {} });
      this._cleanups = [];
    }
  }

  /* --------------------------- quantity input --------------------------- */

  class QuantityInput extends MomentumElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button[data-qty-step]').forEach((btn) => {
        this.on(btn, 'click', (e) => {
          e.preventDefault();
          const dir = btn.dataset.qtyStep === 'up' ? 1 : -1;
          const step = Number(this.input.step) || 1;
          const current = Number(this.input.value) || 0;
          const min = this.input.min !== '' ? Number(this.input.min) : 0;
          const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
          let next = current + dir * step;
          next = Math.min(Math.max(next, min), max);
          if (next === current) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.on(this.input, 'change', () => this.validate());
    }
    validate() {
      const min = this.input.min !== '' ? Number(this.input.min) : 1;
      const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
      const step = Number(this.input.step) || 1;
      let value = Number(this.input.value);
      if (!Number.isFinite(value)) value = min;
      if (step > 1) value = Math.round(value / step) * step;
      value = Math.min(Math.max(value, min), max);
      this.input.value = value;
      publish(events.QUANTITY_CHANGE, { value, input: this.input });
    }
  }

  /* ----------------------------- expose --------------------------------- */

  window.Momentum = {
    utils,
    cart,
    events,
    subscribe,
    publish,
    recentlyViewed,
    Element: MomentumElement
  };

  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* -------------------- page transition + global keys ------------------- */

  utils.onDocumentReady(() => {
    if (T.settings.pageTransition && !utils.prefersReducedMotion()) {
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (!link) return;
        if (link.target === '_blank' || link.hasAttribute('download')) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        const href = link.getAttribute('href') || '';
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
        try {
          const url = new URL(link.href);
          if (url.origin !== window.location.origin) return;
          if (url.pathname === window.location.pathname && url.search === window.location.search) return;
        } catch (err) { return; }
        if (link.closest('[data-no-transition]')) return;
        document.body.classList.add('is-leaving');
      });
      window.addEventListener('pageshow', () => document.body.classList.remove('is-leaving'));
    }

    /* Track product views */
    const pdp = document.querySelector('[data-product-view]');
    if (pdp) recentlyViewed.track(Number(pdp.dataset.productView), pdp.dataset.productHandle);
  });
})();
